/* VastraGharr Luxe 3.0 - cart drawer & cart page enhancements
   - <vg-free-shipping>: animates the free shipping track (foil fill + diamond marker) between Dawn re-renders
   - <vg-cart-upsell>: "Complete the look" row loaded from the recommendations endpoint
     (section vg-cart-upsell, intent=complementary, falls back to related) with one-tap add
   - <vg-cart-sticky>: mobile checkout bar on the cart page (sits above the bottom navigation)
   - <vg-cart-eta>: outside-India text, or delivery dates in India time when Theme settings > Delivery shows dates
   - <vg-cart-recent>: recently viewed products in the empty bag (loads only once it is shown)
   - Fly-to-bag (contract 5): on a successful add the product image flies to the header / bottom-nav bag,
     the bag bumps and the add is announced. Triggers may carry data-vg-fly-source="<selector>" resolved
     inside their closest [data-vg-fly-scope]; otherwise the nearest card / product media image is used.
     Honours Theme settings > vg_enable_fly_to_bag and prefers-reduced-motion (bump / pulse only).
     API: VG.flyToBag(sourceElement, triggerElement) -> Promise, VG.bumpBag(), VG.announceCart(message).
*/
(function () {
  'use strict';

  window.VG = window.VG || {};
  if (window.VG.cartScriptLoaded) return;
  window.VG.cartScriptLoaded = true;

  var VG = window.VG;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var shipProgress = {};
  var upsellCache = new Map();
  var lastUpsellHTML = {};
  var DAY = 86400000;
  var IST_OFFSET = 330 * 60 * 1000; /* India Standard Time is UTC+05:30 all year */

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function cartErrorText() {
    return (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your bag. Please try again.';
  }

  function hasPubSub() {
    return typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  /* With Shiprocket the drawer form has no checkout button, so Enter in its only quantity box submits the form
     natively and leaves for /cart (whose native checkout shows outside India). Dawn's change handler already
     saves the quantity over AJAX. With a native checkout button (Shiprocket off) Dawn's behaviour is kept. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.id !== 'CartDrawer-Form' || !form.elements) return;
    var hasCheckout = Array.prototype.some.call(form.elements, function (element) {
      return element.name === 'checkout';
    });
    if (!hasCheckout) event.preventDefault();
  });

  function headerConfig() {
    if (typeof VG.headerConfig === 'function') return VG.headerConfig() || {};
    var node = document.querySelector('script[data-vg-header-config]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch (e) {
      return {};
    }
  }

  /* ---------- Announcements (aria-live) ---------- */
  VG.announceCart = function (message) {
    var region =
      document.querySelector('[data-vg-cart-announcer]') ||
      document.getElementById('CartDrawer-LiveRegionText') ||
      document.getElementById('cart-live-region-text');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  };

  function addedTitle(data) {
    if (!data) return '';
    if (data.product_title) return data.product_title;
    if (data.items && data.items.length) {
      if (data.items.length > 1) return data.items.length + ' items';
      return data.items[0].product_title || data.items[0].title || '';
    }
    return '';
  }

  /* ---------- Bag bump ---------- */
  function bagTargets() {
    var targets = [];
    var header = document.getElementById('cart-icon-bubble');
    var bottom = document.querySelector('[data-vg-bottom-nav] [data-vg-open-cart], .vg-bottom-nav [data-vg-open-cart]');
    if (header) targets.push(header);
    if (bottom) targets.push(bottom);
    return targets;
  }

  function onScreen(el) {
    if (!el || !el.getClientRects().length) return false;
    var style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  VG.bumpBag = function () {
    bagTargets().forEach(function (el) {
      el.classList.remove('vg-bag-bump');
      void el.offsetWidth;
      el.classList.add('vg-bag-bump');
      window.clearTimeout(el.vgBumpTimer);
      el.vgBumpTimer = window.setTimeout(function () {
        el.classList.remove('vg-bag-bump');
      }, 950);
    });
  };

  /* ---------- Fly-to-bag ---------- */
  var FLY_WINDOW = 9000;
  var pending = null; /* { trigger, time } recorded when an add starts */
  var flight = null; /* Promise while a clone is in the air */

  function flyEnabled() {
    return headerConfig().flyToBag !== false && !reducedMotion.matches;
  }

  function landingTarget() {
    var bottom = document.querySelector('[data-vg-bottom-nav] [data-vg-open-cart], .vg-bottom-nav [data-vg-open-cart]');
    var header = document.getElementById('cart-icon-bubble');
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    var order = mobile ? [bottom, header] : [header, bottom];
    for (var i = 0; i < order.length; i += 1) {
      if (onScreen(order[i])) {
        var icon = order[i].querySelector('.vg-bottom-nav__icon, .svg-wrapper') || order[i];
        return icon;
      }
    }
    return null;
  }

  function visibleRatio(el) {
    if (!el || !el.getClientRects().length) return 0;
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;
    var w = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
    var h = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    if (w <= 0 || h <= 0) return 0;
    return (w * h) / (rect.width * rect.height);
  }

  /* Product photos first, in priority order. A bare <img> only counts when the scope has no product media at all,
     and never an app badge / icon (payment logos in the quick add window are 40px squares). */
  var MEDIA_SELECTOR =
    '[data-vg-vt-target], .product__media-item.is-active img, .product-media-container img, .product__media img, .card__media img, .media img';

  function firstVisible(list, minSide) {
    for (var i = 0; i < list.length; i += 1) {
      var rect = list[i].getBoundingClientRect();
      if (rect.width >= minSide && rect.height >= minSide && visibleRatio(list[i]) > 0.3) return list[i];
    }
    return null;
  }

  function imageIn(scope) {
    if (!scope) return null;
    var media = scope.querySelectorAll(MEDIA_SELECTOR);
    if (media.length) return firstVisible(media, 40);
    var plain = Array.prototype.filter.call(scope.querySelectorAll('img'), function (img) {
      return !img.closest('button, .shopify-app-block, .shiprocket-headless, [class*="payment"], [class*="badge"]');
    });
    return firstVisible(plain, 64);
  }

  /* The product photo when it is scrolled away: it then leaves from the button as a small thumbnail. */
  function productPhoto(trigger) {
    var scope =
      trigger.closest('[data-vg-fly-scope]') ||
      trigger.closest('quick-add-modal, .quick-add-modal, modal-dialog') ||
      trigger.closest('.card-wrapper, .card, .product-card-wrapper') ||
      trigger.closest('product-info, .product, [id^="MainProduct-"], .shopify-section');
    if (!scope) return null;
    var img = scope.querySelector('[data-vg-vt-target], .product__media img, .card__media img, .media img');
    return img && (img.currentSrc || img.src) ? img : null;
  }

  /* Resolve the image to fly for an add-to-cart trigger (contract 5):
     explicit data-vg-fly-source inside [data-vg-fly-scope] -> quick add modal -> scope -> card -> product section.
     Nothing visible means a small gold dot leaves the button instead. */
  function sourceFor(trigger) {
    if (!trigger || !trigger.closest) return null;
    var selector = trigger.getAttribute('data-vg-fly-source');
    var scope = trigger.closest('[data-vg-fly-scope]');
    if (selector) {
      try {
        var explicit = (scope || document).querySelector(selector);
        if (explicit && visibleRatio(explicit) > 0.2) return explicit;
      } catch (e) {
        /* invalid selector: fall back below */
      }
    }
    var modal = trigger.closest('quick-add-modal, .quick-add-modal, modal-dialog');
    if (modal) return imageIn(modal);
    if (scope) {
      var scoped = imageIn(scope);
      if (scoped) return scoped;
    }
    var card = trigger.closest('.card-wrapper, .card, .product-card-wrapper');
    if (card) return imageIn(card);
    return imageIn(trigger.closest('product-info, .product, [id^="MainProduct-"], .shopify-section'));
  }

  function cloneFor(source, trigger, photo) {
    var rect;
    var fromTrigger = false;
    var fly = document.createElement('div');
    fly.className = 'vg-fly';
    fly.setAttribute('aria-hidden', 'true');
    var inner = document.createElement('span');
    inner.className = 'vg-fly__inner';
    fly.appendChild(inner);

    if (source) {
      rect = source.getBoundingClientRect();
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'sync';
      img.src = source.currentSrc || source.src || '';
      if (!img.src) img = null;
      if (img) inner.appendChild(img);
      else fly.classList.add('vg-fly--dot');
    }

    if (!rect || !rect.width) {
      var anchor = trigger && trigger.getBoundingClientRect ? trigger.getBoundingClientRect() : null;
      if (!anchor) return null;
      var thumbSrc = photo ? photo.currentSrc || photo.src : '';
      if (thumbSrc) {
        var thumb = document.createElement('img');
        thumb.alt = '';
        thumb.src = thumbSrc;
        inner.appendChild(thumb);
        fromTrigger = true;
        rect = { left: anchor.left + anchor.width / 2 - 36, top: anchor.top + anchor.height / 2 - 48, width: 72, height: 96 };
      } else {
        var size = 28;
        fly.classList.add('vg-fly--dot');
        fly.style.borderRadius = '50%';
        rect = { left: anchor.left + anchor.width / 2 - size / 2, top: anchor.top + anchor.height / 2 - size / 2, width: size, height: size };
      }
    }

    // Keep the clone a sensible size: a full PDP image is reduced to a 180px thumbnail before take-off.
    var maxSide = 180;
    var ratio = Math.min(1, maxSide / Math.max(rect.width, rect.height));
    var width = rect.width * ratio;
    var height = rect.height * ratio;
    var left = rect.left + (rect.width - width) / 2;
    var top = rect.top + (rect.height - height) / 2;
    fly.style.width = width + 'px';
    fly.style.height = height + 'px';
    fly.style.transform = 'translate(' + left + 'px, ' + top + 'px)';
    return {
      el: fly,
      inner: inner,
      left: left,
      top: top,
      width: width,
      height: height,
      startScale: 1 / ratio,
      fromTrigger: fromTrigger,
    };
  }

  VG.flyToBag = function (source, trigger) {
    if (!flyEnabled()) {
      VG.bumpBag();
      return Promise.resolve(false);
    }
    var target = landingTarget();
    var photo = !source && trigger && trigger.closest ? productPhoto(trigger) : null;
    var clone = target ? cloneFor(source, trigger, photo) : null;
    if (!target || !clone || typeof clone.el.animate !== 'function') {
      VG.bumpBag();
      return Promise.resolve(false);
    }

    document.body.appendChild(clone.el);
    var end = target.getBoundingClientRect();
    var endSize = 26;
    var cx = clone.left + clone.width / 2;
    var cy = clone.top + clone.height / 2;
    var tx = end.left + end.width / 2;
    var ty = end.top + end.height / 2;
    var dx = tx - cx;
    var dy = ty - cy;
    var endScale = endSize / Math.max(clone.width, clone.height);
    var duration = Math.round(Math.min(820, Math.max(560, Math.hypot(dx, dy) * 0.75)));
    var lift = Math.min(90, Math.abs(dx) * 0.18 + 30);

    // Outer element travels horizontally (ease-out), inner vertically with a little lift (ease-in): a soft arc.
    var outer = clone.el.animate(
      [
        { transform: 'translate(' + clone.left + 'px, ' + clone.top + 'px)' },
        { transform: 'translate(' + (clone.left + dx) + 'px, ' + clone.top + 'px)' },
      ],
      { duration: duration, easing: 'cubic-bezier(0.33, 0.9, 0.5, 1)', fill: 'forwards' }
    );
    var startScale = Math.min(clone.startScale, 1.6);
    var inner = clone.inner.animate(
      [
        {
          transform: 'translateY(0) scale(' + (clone.fromTrigger ? 0.5 : startScale) + ')',
          opacity: clone.fromTrigger ? 0 : 1,
          borderRadius: '2px',
        },
        { offset: 0.22, transform: 'translateY(' + -lift + 'px) scale(0.92)', opacity: 1, borderRadius: '10%' },
        { transform: 'translateY(' + dy + 'px) scale(' + endScale + ')', opacity: 0.35, borderRadius: '50%' },
      ],
      { duration: duration, easing: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)', fill: 'forwards' }
    );

    flight = new Promise(function (resolve) {
      var finished = false;
      var finish = function () {
        if (finished) return;
        finished = true;
        if (clone.el.parentNode) clone.el.parentNode.removeChild(clone.el);
        VG.bumpBag();
        flight = null;
        resolve(true);
      };
      inner.onfinish = finish;
      inner.oncancel = finish;
      // Safety net: never hold the drawer back if the animation is throttled (background tab).
      window.setTimeout(finish, duration + 250);
    });
    void outer;
    return flight;
  };

  function record(trigger) {
    if (!trigger) return;
    pending = { trigger: trigger, time: Date.now() };
  }

  function takePending() {
    if (!pending || Date.now() - pending.time > FLY_WINDOW) {
      pending = null;
      return null;
    }
    var entry = pending;
    pending = null;
    return entry;
  }

  /* Starts the flight for the recorded trigger (once). Returns the flight promise, or null. */
  function launchFor(data) {
    var entry = takePending();
    if (!entry) return null;
    var trigger = entry.trigger;
    var title = addedTitle(data);
    if (title && typeof VG.displayTitle === 'function') title = VG.displayTitle(title);
    VG.announceCart(title ? 'Added ' + title + ' to your bag.' : 'Added to your bag.');
    // Inside an open drawer the bag icons are covered: the new line appearing is the feedback.
    if (trigger.closest && trigger.closest('cart-drawer.active')) {
      VG.bumpBag();
      return null;
    }
    return VG.flyToBag(sourceFor(trigger), trigger);
  }

  var TRIGGER_SELECTOR =
    '[data-vg-fly-source], [data-vg-upsell-add], product-form [type="submit"], button[name="add"], .quick-add__submit';

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var trigger = target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;
      if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;
      record(trigger);
      patchCartRenderers();
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || typeof form.matches !== 'function') return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      if (pending && Date.now() - pending.time < 400) return;
      record(event.submitter || form.querySelector('[type="submit"]') || form);
      patchCartRenderers();
    },
    true
  );

  /* Hold Dawn's drawer / notification opening until the clone has landed, so the bag visibly receives it. */
  function wrapRenderer(element) {
    if (!element || element.vgFlyPatched || typeof element.renderContents !== 'function') return;
    element.vgFlyPatched = true;
    var original = element.renderContents;
    element.renderContents = function (parsedState) {
      var self = this;
      var current = flight || launchFor(parsedState);
      if (current && typeof current.then === 'function') {
        current.then(function () {
          original.call(self, parsedState);
        });
        return;
      }
      return original.call(self, parsedState);
    };
  }

  function patchCartRenderers() {
    wrapRenderer(document.querySelector('cart-drawer'));
    wrapRenderer(document.querySelector('cart-notification'));
  }

  if (hasPubSub()) {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || !pending) return;
      launchFor(event.cartData);
    });
    if (PUB_SUB_EVENTS.cartError) {
      window.subscribe(PUB_SUB_EVENTS.cartError, function () {
        pending = null;
      });
    }
  }

  /* ---------- Free shipping bar ---------- */
  if (!customElements.get('vg-free-shipping')) {
    customElements.define(
      'vg-free-shipping',
      class VgFreeShipping extends HTMLElement {
        connectedCallback() {
          // The progress lives on the track (--vg-ship-progress) and drives both the foil fill and the diamond marker.
          var track = this.querySelector('[data-vg-ship-track]');
          if (!track) return;
          var context = this.dataset.context || 'drawer';
          var target = Number(this.dataset.progress) || 0;
          var previous = Object.prototype.hasOwnProperty.call(shipProgress, context) ? shipProgress[context] : null;
          shipProgress[context] = target;

          if (previous === null || previous === target || reducedMotion.matches) return;

          track.classList.add('is-instant');
          track.style.setProperty('--vg-ship-progress', previous);
          void track.offsetWidth;
          track.classList.remove('is-instant');
          window.requestAnimationFrame(function () {
            track.style.setProperty('--vg-ship-progress', target);
          });
        }
      }
    );
  }

  /* ---------- Estimated delivery ---------- */
  if (!customElements.get('vg-cart-eta')) {
    customElements.define(
      'vg-cart-eta',
      class VgCartEta extends HTMLElement {
        connectedCallback() {
          this.text = this.querySelector('[data-vg-eta-text]');
          if (!this.text) return;
          this.render();
        }

        isInternational() {
          var country = (window.Shopify && window.Shopify.country) || this.dataset.country || 'IN';
          return String(country).toUpperCase() !== 'IN';
        }

        render() {
          var strong = document.createElement('strong');
          if (this.isInternational()) {
            // Theme settings > Delivery > Outside India, e.g. "Worldwide delivery in 6–8 working days"
            this.text.textContent = this.dataset.internationalText || 'Worldwide delivery in 6–8 working days';
            this.classList.add('is-international');
            return;
          }
          // "Number of days" (Theme settings > Delivery) is already printed by Liquid; only dates are worked out here.
          if (this.dataset.display !== 'dates') return;
          var calendar = this.dataset.unit !== 'working';

          var cutoff = Math.min(Math.max(parseFloat(this.dataset.cutoff), 0), 24);
          if (isNaN(cutoff)) cutoff = 14;
          var minDays = Math.max(parseInt(this.dataset.min, 10) || 0, 0);
          var maxDays = Math.max(parseInt(this.dataset.max, 10) || minDays, minDays);

          var now = new Date(Date.now() + IST_OFFSET); /* UTC fields now read as India wall-clock time */
          var today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
          var secondsNow = now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds();
          var isSunday = function (ms) {
            return new Date(ms).getUTCDay() === 0;
          };
          var dispatch = today;
          if (isSunday(today) || secondsNow >= Math.round(cutoff * 3600)) {
            dispatch = today + DAY;
            while (isSunday(dispatch)) dispatch += DAY;
          }
          var addWorkingDays = function (start, count) {
            if (calendar) return start + Math.max(count, 0) * DAY;
            var date = start;
            var added = 0;
            while (added < count) {
              date += DAY;
              if (!isSunday(date)) added += 1;
            }
            return date;
          };
          var format = function (ms) {
            try {
              return new Date(ms).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
            } catch (e) {
              return new Date(ms).toDateString();
            }
          };
          var from = addWorkingDays(dispatch, minDays);
          var to = addWorkingDays(dispatch, maxDays);
          // Theme settings > Cart > "Delivery line, before the dates" (printed by Liquid as data-dates-text).
          this.text.textContent = (this.dataset.datesText || 'Estimated delivery') + ' ';
          strong.textContent = from === to ? format(from) : format(from) + ' – ' + format(to);
          this.text.appendChild(strong);
        }
      }
    );
  }

  /* ---------- Recently viewed in the empty bag ---------- */
  if (!customElements.get('vg-cart-recent')) {
    customElements.define(
      'vg-cart-recent',
      class VgCartRecent extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-vg-cart-recent-list]');
          if (!this.list || this.loaded) return;
          var start = () => {
            if ('IntersectionObserver' in window) {
              // The empty bag is usually hidden (closed drawer / non-empty cart page): load only once it shows.
              // A closed drawer sits edge-to-edge with the viewport (intersecting with a zero ratio): wait for real area.
              this.observer = new IntersectionObserver(
                (entries) => {
                  if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) return;
                  this.observer.disconnect();
                  this.load();
                },
                { threshold: [0.01, 0.25] }
              );
              // Watch the visible container: this element stays [hidden] until products arrive.
              this.observer.observe(this.parentElement || document.body);
            } else {
              this.load();
            }
          };
          // vg-search.js (header) provides VG.productMini; every deferred script has run by DOMContentLoaded.
          if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
          else start();
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load() {
          if (this.loaded || !VG.productMini) return;
          this.loaded = true;
          var handles = VG.productMini.recentHandles(parseInt(this.dataset.limit, 10) || 4);
          if (!handles.length) return;
          VG.productMini.list(handles).then((products) => {
            if (!this.isConnected || !products.length) return;
            this.list.textContent = '';
            products.forEach((product) => {
              this.list.appendChild(VG.productMini.render(product, { sizes: '(min-width: 750px) 120px, 25vw' }));
            });
            this.hidden = false;
          });
        }
      }
    );
  }

  /* ---------- Upsell ("Complete the look") ---------- */
  function fetchUpsell(baseUrl, productId, limit, intent) {
    var url =
      baseUrl +
      '?product_id=' +
      encodeURIComponent(productId) +
      '&limit=' +
      encodeURIComponent(limit) +
      '&section_id=vg-cart-upsell&intent=' +
      intent;

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        if (!text) return '';
        var results = parseHTML(text).querySelector('[data-vg-upsell-results]');
        if (!results || Number(results.dataset.count) === 0) return '';
        return results.innerHTML.trim();
      });
  }

  function loadUpsell(baseUrl, productId, limit) {
    var key = baseUrl + '|' + productId + '|' + limit;
    if (upsellCache.has(key)) return upsellCache.get(key);

    var promise = fetchUpsell(baseUrl, productId, limit, 'complementary')
      .then(function (html) {
        return html || fetchUpsell(baseUrl, productId, limit, 'related');
      })
      .catch(function () {
        upsellCache.delete(key);
        return '';
      });

    upsellCache.set(key, promise);
    return promise;
  }

  function cartPageSections() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer) return [];
    var sections = [
      { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' },
    ];
    return sections.filter(function (entry) {
      return entry.section && document.getElementById(entry.id);
    });
  }

  function renderCartPage(sections, html) {
    sections.forEach(function (entry) {
      var source = html && html[entry.section];
      if (!source) return;
      var host = document.getElementById(entry.id);
      if (!host) return;
      var target = host.querySelector(entry.selector) || host;
      var fresh = parseHTML(source).querySelector(entry.selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });

    var cartItems = document.querySelector('cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (cartItems) cartItems.classList.remove('is-empty');
    if (footer) footer.classList.remove('is-empty');
  }

  if (!customElements.get('vg-cart-upsell')) {
    customElements.define(
      'vg-cart-upsell',
      class VgCartUpsell extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-vg-upsell-list]');
          var productId = this.dataset.productId;
          if (!this.list || !productId) return;

          this.context = this.dataset.context || 'drawer';
          this.inCart = (this.dataset.cartKey || '').split('-').filter(Boolean);

          if (!this.bound) {
            this.bound = true;
            // Keep variant selects from triggering Dawn's cart quantity handler on <cart-items>.
            this.addEventListener('change', function (event) {
              event.stopPropagation();
            });
            this.addEventListener('click', this.onClick.bind(this));
          }

          if (lastUpsellHTML[this.context]) this.render(lastUpsellHTML[this.context], true);

          loadUpsell(this.dataset.url, productId, this.dataset.limit || 8).then((html) => {
            if (!this.isConnected) return;
            this.render(html, false);
          });
        }

        render(html, placeholder) {
          if (!html) {
            if (!placeholder) this.hidden = true;
            return;
          }

          this.list.innerHTML = html;
          var inCart = this.inCart;
          this.list.querySelectorAll('[data-product-id]').forEach(function (item) {
            if (inCart.indexOf(item.dataset.productId) > -1) item.remove();
          });

          var count = this.list.children.length;
          this.hidden = count === 0;
          this.classList.toggle('is-single', count === 1);
          if (!placeholder) lastUpsellHTML[this.context] = html;
        }

        onClick(event) {
          var button = event.target.closest('[data-vg-upsell-add]');
          if (!button || !this.contains(button)) return;
          event.preventDefault();
          if (button.getAttribute('aria-disabled') === 'true') return;

          var card = button.closest('[data-vg-upsell-card]');
          var select = card ? card.querySelector('[data-vg-upsell-variant]') : null;
          var variantId = Number(select ? select.value : button.dataset.variantId);
          var errorEl = card ? card.querySelector('[data-vg-upsell-error]') : null;
          if (!variantId) return;

          if (errorEl) errorEl.textContent = '';
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('is-loading', 'loading');

          var drawer = document.querySelector('cart-drawer');
          var useDrawer =
            this.context === 'drawer' && drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
          var sections = useDrawer ? drawer.getSectionsToRender() : cartPageSections();

          var body = { items: [{ id: variantId, quantity: 1 }] };
          if (sections.length) {
            body.sections = sections.map(function (entry) {
              return entry.section || entry.id;
            });
            body.sections_url = window.location.pathname;
          }

          var addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
          var self = this;

          fetch(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          })
            .then(function (response) {
              return response.json().then(function (data) {
                return { ok: response.ok, data: data };
              });
            })
            .then(function (result) {
              var data = result.data || {};
              if (!result.ok || data.status) {
                throw new Error(data.description || data.message || cartErrorText());
              }

              // Success state ("Added" with a check) before the bag re-renders around it.
              button.classList.remove('is-loading', 'loading');
              button.classList.add('is-added');
              var flightDone = launchFor(data) || Promise.resolve();
              var pause = new Promise(function (resolve) {
                window.setTimeout(resolve, reducedMotion.matches ? 250 : 700);
              });

              return Promise.all([flightDone, pause]).then(function () {
                if (useDrawer) {
                  drawer.classList.remove('is-empty');
                  drawer.renderContents(data);
                  window.setTimeout(function () {
                    var container = document.getElementById('CartDrawer');
                    var focusTarget = container && container.querySelector('.cart-item__name');
                    if (container && focusTarget && typeof window.trapFocus === 'function') {
                      window.trapFocus(container, focusTarget);
                    }
                  }, 60);
                } else if (sections.length) {
                  renderCartPage(sections, data.sections);
                  var drawerItems = document.querySelector('cart-drawer-items');
                  if (drawerItems && typeof drawerItems.onCartUpdate === 'function') drawerItems.onCartUpdate();
                } else {
                  window.location.href = (window.routes && window.routes.cart_url) || '/cart';
                  return;
                }

                if (hasPubSub() && typeof window.publish === 'function') {
                  // Sections were re-rendered above, so CartItems can skip its own refetch.
                  window.publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'cart-items',
                    cartData: data,
                    variantId: variantId,
                  });
                }

                if (window.VG && typeof window.VG.refreshCartCount === 'function') window.VG.refreshCartCount();
                if (self.isConnected) {
                  var item = button.closest('[data-product-id]');
                  if (item) item.remove();
                  if (!self.list.children.length) self.hidden = true;
                }
              });
            })
            .catch(function (error) {
              pending = null;
              if (errorEl) errorEl.textContent = (error && error.message) || cartErrorText();
              button.removeAttribute('aria-disabled');
              button.classList.remove('is-loading', 'loading', 'is-added');
            });
        }
      }
    );
  }

  /* ---------- Cart page mobile checkout bar ---------- */
  if (!customElements.get('vg-cart-sticky')) {
    customElements.define(
      'vg-cart-sticky',
      class VgCartSticky extends HTMLElement {
        connectedCallback() {
          this.target = document.getElementById(this.dataset.target || 'VgCartCheckout');
          this.button = this.querySelector('[data-vg-cart-sticky-go]');
          if (!this.target || !this.button) return;

          this.onGo = (event) => {
            event.preventDefault();
            this.target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
            // Skip the checkout option hidden for this time zone (its Fastrr button is rendered there too).
            var focusable = Array.prototype.find.call(
              this.target.querySelectorAll('button:not([disabled]), a[href], .sr-headless-checkout'),
              function (el) {
                return !el.closest('[hidden]') && el.getClientRects().length > 0;
              }
            );
            if (focusable && typeof focusable.focus === 'function') {
              window.setTimeout(function () {
                focusable.focus({ preventScroll: true });
              }, reducedMotion.matches ? 0 : 450);
            }
          };
          this.button.addEventListener('click', this.onGo);

          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
              var entry = entries[0];
              var below = entry.boundingClientRect.top > 0;
              this.setVisible(!entry.isIntersecting && below);
            });
            this.observer.observe(this.target);
          }
        }

        setVisible(visible) {
          this.classList.toggle('is-visible', visible);
          // Lift floating UI (WhatsApp button, toasts) above the bar while it shows on mobile.
          var showing = visible && window.matchMedia('(max-width: 749px)').matches;
          document.documentElement.style.setProperty('--vg-float-offset', showing ? this.offsetHeight + 'px' : '0px');
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.button && this.onGo) this.button.removeEventListener('click', this.onGo);
          document.documentElement.style.setProperty('--vg-float-offset', '0px');
        }
      }
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', patchCartRenderers, { once: true });
  else patchCartRenderers();
})();
